import { Link } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import { BUSINESS_CONFIG } from '../config/businessConfig';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerColumns = [
    {
      title: BUSINESS_CONFIG.name,
      links: [
        { label: BUSINESS_CONFIG.shortAddress, icon: MapPin, custom: true }
      ],
      description: BUSINESS_CONFIG.tagline
    },
    {
      title: 'Product',
      links: [
        { label: 'Emissions Tracker Dashboard', path: '/product' },
        { label: 'Grant Eligibility Tool', path: '/grant-tool' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', path: '/about' },
        { label: 'Contact', path: '/contact' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', path: '/privacy' },
        { label: 'Terms & Conditions', path: '/terms' },
        { label: BUSINESS_CONFIG.abnFormatted, custom: true }
      ]
    }
  ];

  return (
    <footer className="bg-[#0F172A] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Main Footer Content - 4 Columns */}
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {footerColumns.map((column, index) => (
            <div key={index}>
              <h3 className="text-white font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {column.title}
              </h3>

              {index === 0 && (
                <div className="mb-4">
                  <p className="text-slate-400 text-sm leading-relaxed mb-3">
                    {column.description}
                  </p>
                </div>
              )}

              <ul className="space-y-3">
                {column.links.map((link, linkIndex) => {
                  const Icon = link.icon;

                  if (link.custom) {
                    return (
                      <li key={linkIndex} className="text-slate-400 text-sm flex items-center space-x-2">
                        {Icon && <Icon className="w-4 h-4" />}
                        <span>{link.label}</span>
                      </li>
                    );
                  }

                  return (
                    <li key={linkIndex}>
                      <Link
                        to={link.path}
                        className="text-slate-400 hover:text-[#74A57F] transition-colors text-sm"
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {index === 0 && (
                <p className="text-[#74A57F] text-sm mt-4 font-medium">
                  {BUSINESS_CONFIG.slogan}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Divider line removed to save space as it was redundant with the background contrast */}

        {/* Global Disclaimer */}
        <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800 mb-8">
          <p className="text-slate-400 text-xs leading-relaxed" data-testid="footer-global-disclaimer">
            <strong className="text-slate-300">Important Disclaimer:</strong> {BUSINESS_CONFIG.name} is a comprehensive carbon data management platform.
            The Emissions Tracker Dashboard and reporting tools are designed to assist businesses in tracking and visualizing Scope 1 and 2 emissions data.
            Information provided does not constitute financial, legal, or professional advice. Compliance and final reporting should always be verified by a qualified ESG auditor.
            Users are responsible for the accuracy of their data entries and for ensuring compliance with relevant state and national regulations.
            Grant eligibility and funding amounts are subject to change by government bodies. {BUSINESS_CONFIG.name} makes no guarantees regarding grant approvals or contract awards.
          </p>
        </div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} {BUSINESS_CONFIG.name}. {BUSINESS_CONFIG.abnFormatted}. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            Made with ❤️ in {BUSINESS_CONFIG.address.suburb}, {BUSINESS_CONFIG.address.state}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;